/**
 * Проверка покупки: вердикт → последствие → альтернатива (блок B-13).
 *
 * Это бесплатная часть продукта и его главный вопрос: «могу ли я себе это
 * позволить». Считает ядро, а не модель, — поэтому без квот и без сети.
 *
 * Три правила экрана.
 *
 * · **Рабочие данные не меняются.** Расчёт — это расчёт; запись покупки
 *   остаётся отдельным решением человека, и кнопка названа своими словами.
 * · **Ограничения расчёта показываются рядом с ответом.** «Новый срок кредита
 *   не пересчитан» — часть ответа, а не сноска: без неё вывод выглядит точнее,
 *   чем он есть.
 * · **«Записать покупку» есть при любом вердикте.** Приложение считает
 *   последствия, а решает человек; отказ без выхода превращает советчика в
 *   вахтёра. Будущую покупку записывать рано — её записывают, когда она
 *   случилась. Прежнее «Всё равно купить» читалось как сама покупка.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatMoney, runScenario,
  type ActionScenario, type Money, type PlainDate, type ScenarioResult,
} from '@skoka/core';
import { AmountInput } from './amount.js';
import { SheetWrap } from './sheet.js';
import { DateButton, dateText } from './date-picker.js';
import { Tip } from './tip.js';
import { toolInput } from './advisor.js';
import { parseAmountInput, todayPd, type AppState } from './store.js';
import { dayMonth } from './text.js';
import { track } from './track.js';

const VERDICT: Record<ScenarioResult['verdict'], { title: string; tone: string }> = {
  safe: { title: 'Можно', tone: '' },
  tight: { title: 'Впритык', tone: ' warn' },
  unsafe: { title: 'Не стоит', tone: ' alarm' },
};

export function CheckPurchase({ state, onClose, onBuy }: {
  state: AppState;
  onClose: () => void;
  /** «Записать покупку» — открывает запись расхода с той же суммой и датой. */
  onBuy: (amount: Money, date: PlainDate) => void;
}) {
  const today = todayPd();
  const [raw, setRaw] = useState('');
  const [when, setWhen] = useState<PlainDate>(today);
  const amount = parseAmountInput(raw);

  /**
   * Расчёт идёт на каждое изменение и ничего не сохраняет.
   *
   * Он дешёвый и целиком локальный: держать ради него кнопку «посчитать»
   * значило бы заставлять человека просить то, что уже готово.
   */
  /**
   * Чего расчёт не знает.
   *
   * Симуляция считает деньгами, и «не знаю» превращается в ноль. Ноль на
   * жизнь означает, что человек не тратит ничего, — и тогда хватает почти на
   * что угодно. Ответ «Можно» на такой картине хуже отсутствия ответа: он
   * выглядит посчитанным.
   */
  const unknown = useMemo(() => {
    const out: string[] = [];
    if (!state.baselines.need.known) out.push('обычные траты на повседневные нужды');
    if (state.incomeStreams.filter((s) => s.isActive).length === 0) out.push('доход');
    return out;
  }, [state]);

  const result = useMemo((): ScenarioResult | null => {
    if (amount <= 0) return null;
    const scenario: ActionScenario = { kind: 'purchase', amount, date: when };
    return runScenario({
      ...toolInput(state),
      needBaseline: state.needBaseline,
      wantBaseline: state.wantBaseline,
      amount,
    }, scenario);
  }, [state, amount, when]);
  // В отчёт — один раз за открытие, когда ответ появился, а не на каждую цифру.
  const counted = useRef(false);
  useEffect(() => {
    if (result && !counted.current) { counted.current = true; track('check_purchase'); }
  }, [result]);

  const future = when > today;

  return (
    <SheetWrap title="Проверить покупку" onClose={onClose}
      // Что считает лист и что расчёт ничего не записал — у заголовка, а не
      // абзацами под формой и под ответом.
      tip={<>
        Назови сумму — посчитаю, что останется до зарплаты и в какой день
        будет тяжелее всего. Это считает приложение, а не ИИ: без ограничений
        и без сети.
        {result !== null && (future
          ? ' Расчёт ничего не записал. Когда купишь, запиши расход — и картина обновится.'
          : ' Расчёт ничего не записал. «Записать покупку» откроет запись расхода — решение остаётся твоим.')}
      </>}>
      <div className="form">
        <label>Сколько стоит
          <AmountInput value={raw} onChange={setRaw} placeholder="5 000" /></label>

        <div className="pick"><div className="k">Когда</div>
          <div className="chips">
            <button className={`chip${when === today ? ' on' : ''}`}
              onClick={() => setWhen(today)}>Сегодня</button>
            <DateButton className={`chip date${when !== today ? ' on' : ''}`} label="Дата покупки"
              value={when} min={today} onChange={(d) => setWhen(d as PlainDate)}>
              {when !== today ? dateText(when, today) : 'Другой день'}
            </DateButton>
          </div>
        </div>
      </div>

      {result !== null && (
        <Answer result={result} unknown={unknown} today={today} future={future}
          onBuy={() => onBuy(amount, when)} />
      )}
    </SheetWrap>
  );
}

function Answer({ result, unknown, onBuy, today, future }: {
  result: ScenarioResult; unknown: string[]; onBuy: () => void;
  today: PlainDate;
  /** Покупка назначена на будущее — записывать её рано. */
  future: boolean;
}) {
  // Без обычных трат вердикта нет. «Можно» на неполной картине — это не
  // осторожный ответ, а неверный: он посчитан так, будто жить не на что.
  const v = unknown.length > 0
    ? { title: 'Пока не скажу', tone: ' warn' }
    : VERDICT[result.verdict];
  return (
    <>
      {unknown.length > 0 && (
        <div className="note warn" style={{ marginTop: 0 }}>
          Не знаю главного: {unknown.join(', ')}. Без этого ответить «можно» или
          «нельзя» нечем — из дохода вычесть нечего, и хватит на что угодно.
        </div>
      )}

      <div className={`li${v.tone}`}><div className="nm">
        <b>{v.title}</b>
        <span>
          {/* Последствие называется числом и днём: «впритык» без даты — это
              оценка, которую нечем проверить. */}
          {/* Два числа — за разные сроки, и каждый назван: иначе «−164,75»
              рядом с «34 604,32» читается как противоречие. */}
          {unknown.length > 0 ? 'Считаю только по известному: ' : ''}
          свободных денег до ближайшего прихода останется {formatMoney(result.effects.leftAfter)}
          {unknown.length === 0 && (
            <>; самый низкий остаток на счетах за три месяца — {formatMoney(result.effects.minBalance)},
              {' '}{dayMonth(result.effects.minBalanceDate, today)}</>
          )}
        </span>
      </div></div>

      {unknown.length === 0 && result.alternatives.length > 0 && (
        <>
          <div className="blkhead"><b>Если подождать</b></div>
          {result.alternatives.map((a) => (
            <div className="li" key={a.label}><div className="nm">
              <b>{a.label}</b>
              <span>{a.effect}</span>
            </div></div>
          ))}
        </>
      )}

      {/* Чего расчёт не знает — обязательно к показу: без этих строк вывод
          выглядит точнее, чем он есть. */}
      {result.limits.length > 0 && (
        <div className="note warn">{result.limits.join('. ')}</div>
      )}

      {/* Записать можно только то, что уже случилось: будущую покупку
          приложение не записывает. */}
      {!future && <button className="btn pri" onClick={onBuy}>Записать покупку</button>}
    </>
  );
}
